import React, { useState, useCallback, useRef, useEffect, useContext } from 'react'

import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import camera from '@console/common/assets/camera.svg'
import refresh from '@console/common/assets/refresh.svg'
import Button from '@console/common/components/Button'
import Cascade, { SelectOptions } from '@console/common/components/Cascade'
import TextInput, { InputTypes } from '@console/common/components/TextInput'
import { AnalyticsContext, AnalyticsEvent } from '@console/common/contexts/AnalyticsContext'
import { ModalContext } from '@console/common/contexts/ModalContext'
import { Language } from '@console/common/services/api'
import { upload } from '@console/common/services/upload'

import Header from '../../../components/Header'
import Popup from '../../../components/Popup'
import Spinner from '../../../components/Spinner'
import { createCustomer } from '../../../services/api'
import {
  Container,
  DropzoneContainer,
  RefreshLogo,
  FieldNames,
  CascadeRow,
  ButtonContainer
} from './styles'

const Creation: React.FC = () => {
  const { t } = useTranslation(['settings'])
  const { isOpen, openModal } = useContext(ModalContext)
  const [loading, setLoading] = useState<boolean>(false)
  const [logo, setLogo] = useState<string | null>(null)
  const [name, setName] = useState<string>('')
  const [slug, setSlug] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [language, setLanguage] = useState<SelectOptions | null>(null)
  const [waterMark, setWaterMark] = useState<SelectOptions | null>(null)
  const [status, setStatus] = useState<SelectOptions | null>(null)
  const [validity, setValidity] = useState<boolean>(false)
  const nameRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLDivElement>(null)
  const { analytics } = useContext(AnalyticsContext)
  const [statusCode, setStatusCode] = useState<number | null>(null)

  useEffect(() => {
    setSlug(
      name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase()
    )
  }, [name])

  useEffect(() => {
    analytics?.track(AnalyticsEvent.SETTINGS_CUSTOMERS_CREATE)
  }, [analytics])

  useEffect(() => {
    const nameValidity = (nameRef.current?.children[0] as HTMLInputElement)?.checkValidity()
    const emailValidity = (emailRef.current?.children[0] as HTMLInputElement)?.checkValidity()
    setValidity(
      nameValidity && emailValidity && !!slug && language?.value && waterMark?.value && status
    )
  }, [name, slug, email, language, waterMark, status])

  const LanguageOptions = [
    {
      value: Language.EN,
      label: Language.EN
    },
    {
      value: Language.PT,
      label: Language.PT
    }
  ]

  const WatermarkOptions = [
    {
      value: 'bottomLeft',
      label: t('customers.creation.bottomLeft')
    },
    {
      value: 'bottomRight',
      label: t('customers.creation.bottomRight')
    },
    {
      value: 'topLeft',
      label: t('customers.creation.topLeft')
    },
    {
      value: 'topRight',
      label: t('customers.creation.topRight')
    }
  ]

  const StatusOptions = [
    {
      value: true,
      label: t('customers.active')
    },
    {
      value: false,
      label: t('customers.inactive')
    }
  ]

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file: File) => {
      upload(file, 'logo', setLogo)
    })
  }, [])

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: 'image/*',
    noClick: true,
    noKeyboard: true,
    noDragEventsBubbling: true,
    maxFiles: 1
  })

  const triggerCreation = async () => {
    if (!validity) return
    if (isOpen) return
    setLoading(true)
    const customer = await createCustomer(
      name,
      slug,
      email,
      language?.value,
      waterMark?.value,
      status?.value,
      logo || undefined
    )

    if (customer && 'error' in customer) {
      setStatusCode(customer.error)
    } else {
      analytics?.track(AnalyticsEvent.SETTINGS_CUSTOMERS_CREATE_CREATED, { name })
      setStatusCode(null)
    }
    setLoading(false)
    openModal()
  }

  return (
    <>
      <Popup statusCode={statusCode} message='customers.creation' route='/settings/customers' />
      <Container>
        <Header returnPage='/settings/customers' />
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          {logo ? (
            <>
              <img src={logo} alt='logo' />
              <RefreshLogo
                src={refresh}
                onClick={open}
                onKeyPress={open}
                alt=''
                role='presentation'
              />
            </>
          ) : (
            <div role='button' onClick={open} onKeyPress={open} tabIndex={0}>
              <img src={camera} alt='upload' />
            </div>
          )}
        </DropzoneContainer>
        <FieldNames>{t('customers.creation.name')}</FieldNames>
        <div ref={nameRef}>
          <TextInput
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder={t('customers.creation.nameExample')}
            required
            width='487px'
            maxLength={255}
            disabled={loading}
          />
        </div>
        <FieldNames>{t('customers.creation.slug')}</FieldNames>
        <TextInput
          value={slug}
          onChange={(e) => setSlug(e.currentTarget.value)}
          required
          disabled
          width='487px'
          maxLength={255}
        />
        <FieldNames>{t('customers.creation.email')}</FieldNames>
        <div ref={emailRef}>
          <TextInput
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            placeholder={t('customers.creation.emailExample')}
            required
            type={InputTypes.EMAIL}
            width='487px'
            disabled={loading}
            maxLength={255}
          />
        </div>
        <CascadeRow>
          <div>
            <FieldNames>{t('customers.creation.language')}</FieldNames>
            <Cascade options={LanguageOptions} onChange={(option) => setLanguage(option)} />
          </div>
          <div>
            <FieldNames>{t('customers.creation.watermarkPosition')}</FieldNames>
            <Cascade options={WatermarkOptions} onChange={(option) => setWaterMark(option)} />
          </div>
        </CascadeRow>
        <FieldNames>{t('customers.creation.status')}</FieldNames>
        <Cascade options={StatusOptions} onChange={(option) => setStatus(option)} />
        <ButtonContainer>
          <Button onClick={triggerCreation} disabled={!validity}>
            {loading ? (
              <>
                {t('customers.creation.creating')}
                <Spinner />
              </>
            ) : (
              t('customers.creation.create')
            )}
          </Button>
        </ButtonContainer>
      </Container>
    </>
  )
}

export default Creation
